import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '$partials/ui/index.js';
import { formatLinkBlockToUrl } from '$utilities/data.js';
import { GridBlock } from '$partials/grid/layout/index.js';
import { isLocalURL } from '$utilities/browser.js';
import { linearScale } from '$utilities/scale.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const GridTextBlock = (props) => {
    const observerRef = useRef();
    const [isVisible, setIsVisible] = useState(0);
    const [progress, setProgress] = useState(0);

    let didScroll = false;

    useEffect(() => {
        if (observerRef.current && typeof IntersectionObserver === 'function') {
            const handler = entries => {
                if (entries[0]) {
                    setIsVisible(entries[0].isIntersecting);
                }
            };

            const observer = new IntersectionObserver(handler, { rootMargin: '0px' });

            observer.observe(observerRef.current);

            return () => {
                observer.disconnect();
            };
        }

        return () => { };
    }, [observerRef.current]);

    const measureProgress = () => {
        if (observerRef.current) {
            const r = observerRef.current.getBoundingClientRect();
            const startOffset = 50;
            setProgress(linearScale(r.top + startOffset, window.innerHeight, (window.innerHeight / 2) + startOffset, 0, 100));
        }
    };

    const handleScroll = () => {
        if (!didScroll) {
            window.requestAnimationFrame(() => {
                if (isVisible) { measureProgress(); }
                didScroll = false;
            });
            didScroll = true;
        }
    };

    // hooks
    useEffect(() => {
        if (isVisible) {
            measureProgress();
            window.addEventListener('scroll', handleScroll);
        } else {
            window.removeEventListener('scroll', handleScroll);
        }

        return () => window.removeEventListener('scroll', handleScroll);
    }, [isVisible]);

    // misc
    const href = formatLinkBlockToUrl(props?.ctaLink);
    const isLocal = isLocalURL(href);

    return (
        <GridBlock {...props}>
            <div className='w-full h-full flex flex-col gap-20 items-center justify-center px-layout-item py-60 sm:py-layout-item'>
                {props?.gridText && (
                    <div className='text-center text-pretty max-w-[30em]'>
                        <p
                            className='text-highlight type-style-4'
                            ref={observerRef}
                            style={{
                                backgroundSize: `${progress}% 100%`,
                                backgroundImage: `linear-gradient(to right, ${props?.highlightColour}, ${props?.highlightColour})`
                            }}>
                            {props.gridText}
                        </p>
                    </div>
                )}

                {(props?.ctaLabel && href) && (
                    <Button
                        as='a'
                        className='inline-block h-[48px] leading-[48px] px-[30px] rounded-[24px] bg-[#000] text-[#fff]'
                        href={href}
                        rel={isLocal ? null : 'noopener noreferrer'}
                        target={isLocal ? null : '_blank'}>
                        {props.ctaLabel}
                    </Button>
                )}
            </div>
        </GridBlock>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    className: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.object,
    gridText: PropTypes.string,
    height: PropTypes.number.isRequired,
    highlightColour: PropTypes.string,
    id: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
};

GridTextBlock.propTypes = propTypes;

export default GridTextBlock;
